import { useState } from 'react';
import { Lock } from 'lucide-react';
import { api } from '../lib/api';
import { useAction, useApi } from '../lib/useApi';
import { Empty, ErrorBox, Field, Loading, Notice, PageHeader, StatusBadge, fmtDate } from '../components/ui';

interface TutorMe {
  tutor: { display_name: string; status: string; bio_for_parents: string | null; suspended_reason: string | null };
  checks: Array<{ check_type: string; status: string; checked_at: string }>;
  vetting: { complete: boolean; missing: string[]; failed: boolean };
}
interface Session {
  id: string;
  title: string;
  track: string;
  age_band: string;
  starts_at: string;
  status: string;
  booked: number;
  capacity: number;
  min_children: number;
}

export function TutorConsolePage() {
  const me = useApi<TutorMe>('/tutor/me');
  const sessions = useApi<{ sessions: Session[]; canHost: boolean }>('/tutor/sessions');
  const act = useAction();
  const [roster, setRoster] = useState<Record<string, Array<{ first_name: string; age_band: string }>>>({});
  const [form, setForm] = useState({ title: '', track: 'kids', ageBand: '7-9', startsAt: '', durationMinutes: 30, capacity: 6 });

  if (me.loading) return <Loading />;
  if (me.error || !me.data) return <ErrorBox error={me.error} />;
  const t = me.data.tutor;
  const approved = t.status === 'approved';

  return (
    <>
      <PageHeader title="Tutor console" lead="Live Circles are always group sessions (2+ children), always recorded, and parents can join at any time. There is no messaging with children or families." />
      <Notice text={act.notice} />
      <ErrorBox error={act.error} />
      <div className="grid grid-2">
        <section className="card stack">
          <div className="spread">
            <h2 className="yo" style={{ margin: 0 }}>{t.display_name}</h2>
            <StatusBadge status={t.status} />
          </div>
          {t.suspended_reason && <p className="alert error small">{t.suspended_reason}</p>}
          <h3>Vetting</h3>
          <ul className="small">
            {me.data.checks.map((c, i) => (
              <li key={i}>
                {c.check_type.replace('_', ' ')} · <StatusBadge status={c.status} /> · {fmtDate(c.checked_at, false)}
              </li>
            ))}
          </ul>
          {!me.data.vetting.complete && <p className="small">Still needed: {me.data.vetting.missing.join(', ') || 'safeguarding review'}</p>}
          {!approved && <p className="alert info small">You can create sessions once our safeguarding team approves you.</p>}
        </section>
        {approved && (
          <form
            className="card stack"
            onSubmit={async (e) => {
              e.preventDefault();
              await act.run(() => api('/tutor/sessions', { body: { ...form, startsAt: new Date(form.startsAt).toISOString() } }), 'Circle scheduled.');
              sessions.reload();
            }}
          >
            <h2>Schedule a Live Circle</h2>
            <Field id="tt" label="Title">
              <input id="tt" className="yo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </Field>
            <div className="row">
              <select aria-label="Track" value={form.track} onChange={(e) => setForm({ ...form, track: e.target.value, ageBand: e.target.value === 'teen' ? '13-15' : '7-9' })}>
                <option value="kids">Kids</option>
                <option value="teen">Teen</option>
              </select>
              <select aria-label="Age band" value={form.ageBand} onChange={(e) => setForm({ ...form, ageBand: e.target.value })}>
                {(form.track === 'teen' ? ['13-15', '16-17'] : ['4-6', '7-9', '10-12']).map((b) => (
                  <option key={b}>{b}</option>
                ))}
              </select>
              <input aria-label="Capacity" type="number" min={2} max={10} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} style={{ width: 80 }} />
            </div>
            <Field id="ts" label="Starts at">
              <input id="ts" type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} required />
            </Field>
            <p className="small muted inline-icon">
              <Lock size={15} strokeWidth={1.75} aria-hidden="true" />
              <span>Recording and parent access are always on and cannot be changed.</span>
            </p>
            <button className="btn" disabled={act.busy}>
              Schedule
            </button>
          </form>
        )}
      </div>
      <h2 style={{ marginTop: 20 }}>Your circles</h2>
      {sessions.data?.sessions.length === 0 && <Empty>No circles yet.</Empty>}
      <div className="grid grid-2">
        {sessions.data?.sessions.map((s) => (
          <div key={s.id} className="card stack">
            <div className="spread">
              <strong className="yo">{s.title}</strong>
              <StatusBadge status={s.status} />
            </div>
            <span className="small muted">
              {fmtDate(s.starts_at)} · ages {s.age_band} · {s.booked}/{s.capacity} booked
            </span>
            <div className="row">
              {['scheduled', 'live'].includes(s.status) && (
                <button
                  className="btn small"
                  disabled={s.booked < s.min_children}
                  title={s.booked < s.min_children ? `Needs at least ${s.min_children} children booked` : undefined}
                  onClick={async () => {
                    const r = await act.run(() => api<{ joinUrl: string }>(`/tutor/sessions/${s.id}/start`, { method: 'POST', body: {} }));
                    if (r) window.open(r.joinUrl, '_blank', 'noopener');
                    sessions.reload();
                  }}
                >
                  {s.status === 'live' ? 'Rejoin' : 'Start (recorded)'}
                </button>
              )}
              <button
                className="btn small ghost"
                onClick={async () => {
                  const r = await act.run(() => api<{ roster: Array<{ first_name: string; age_band: string }> }>(`/tutor/sessions/${s.id}/roster`));
                  if (r) setRoster({ ...roster, [s.id]: r.roster });
                }}
              >
                Roster
              </button>
              {s.status === 'live' && (
                <button className="btn small secondary" onClick={async () => { await act.run(() => api(`/tutor/sessions/${s.id}/complete`, { method: 'POST', body: {} })); sessions.reload(); }}>
                  End
                </button>
              )}
              {s.status === 'scheduled' && (
                <button className="btn small ghost" onClick={async () => { await act.run(() => api(`/tutor/sessions/${s.id}/cancel`, { method: 'POST', body: {} })); sessions.reload(); }}>
                  Cancel
                </button>
              )}
            </div>
            {roster[s.id] && (
              <p className="small">
                {roster[s.id]!.length ? roster[s.id]!.map((r) => `${r.first_name} (${r.age_band})`).join(', ') : 'No bookings yet.'}
              </p>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
